import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur, fmtDate, fmtTime } from "@/lib/format";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, RotateCcw, CalendarClock, Clock, Check } from "lucide-react";

const RESCHEDULE_MIN_HOURS = 4;
const dstr = (d) => {
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
};
const hoursUntil = (iso) => (new Date(iso).getTime() - Date.now()) / 3600000;

export default function PortalAppointments() {
  const nav = useNavigate();
  const [appts, setAppts] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [reschedule, setReschedule] = useState(null); // appt being rescheduled

  const load = useCallback(() => api.get("/portal/appointments").then((r) => setAppts(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (a) => {
    try { await api.put(`/portal/appointments/${a.id}`, { action: "cancel" }); toast.success("Appuntamento cancellato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossibile cancellare"); }
  };

  if (!appts) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--hi-gold))]" /></div>;
  const now = new Date();
  const upcoming = appts.filter((a) => new Date(a.start) >= now && !["cancelled", "completed", "no_show"].includes(a.status));
  const past = appts.filter((a) => !upcoming.includes(a));
  const list = tab === "upcoming" ? upcoming : past;

  return (
    <div>
      <h1 className="font-heading text-2xl font-semibold mb-4">I miei appuntamenti</h1>
      <Tabs value={tab} onValueChange={setTab} className="mb-4"><TabsList className="w-full"><TabsTrigger value="upcoming" className="flex-1" data-testid="appt-tab-upcoming">Prossimi</TabsTrigger><TabsTrigger value="past" className="flex-1" data-testid="appt-tab-past">Passati</TabsTrigger></TabsList></Tabs>
      <div className="space-y-3">
        {list.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">Nessun appuntamento.</Card>}
        {list.map((a) => {
          const canReschedule = hoursUntil(a.start) >= RESCHEDULE_MIN_HOURS;
          return (
          <Card key={a.id} className="p-4" data-testid={`portal-appt-${a.id}`}>
            <div className="flex justify-between items-start">
              <div><div className="font-medium">{fmtDate(a.start, { weekday: "long", day: "2-digit", month: "long" })}</div><div className="text-sm text-muted-foreground">{fmtTime(a.start)} · {a.operator_name || "Primo disponibile"}</div></div>
              <StatusBadge status={a.status} />
            </div>
            <div className="text-sm mt-2">{(a.service_names || []).join(", ")}</div>
            <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
              <span className="font-semibold">{eur(a.price)}</span>
              {tab === "upcoming" ? (
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="outline" size="sm" disabled={!canReschedule} onClick={() => setReschedule(a)} data-testid={`portal-reschedule-${a.id}`}>
                    <CalendarClock className="h-4 w-4 mr-1" /> Riprogramma
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`portal-cancel-${a.id}`}>Cancella</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Cancellare l'appuntamento?</AlertDialogTitle><AlertDialogDescription>Lo slot tornerà disponibile. La cancellazione è consentita entro i termini previsti dal salone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Torna indietro</AlertDialogCancel><AlertDialogAction onClick={() => cancel(a)}>Conferma cancellazione</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => nav("/portale/prenota")} data-testid={`portal-rebook-${a.id}`}><RotateCcw className="h-4 w-4 mr-1" /> Riprenota</Button>
              )}
            </div>
            {tab === "upcoming" && !canReschedule && (
              <p className="text-xs text-muted-foreground mt-2">La riprogrammazione online è disponibile fino a {RESCHEDULE_MIN_HOURS} ore prima. Contatta il salone per modifiche last-minute.</p>
            )}
          </Card>
          );
        })}
      </div>

      <RescheduleDialog appt={reschedule} onClose={() => setReschedule(null)} onDone={() => { setReschedule(null); load(); }} />
    </div>
  );
}

function RescheduleDialog({ appt, onClose, onDone }) {
  const [date, setDate] = useState(dstr(new Date(Date.now() + 86400000)));
  const [avail, setAvail] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAvail = useCallback(async () => {
    if (!appt) return;
    setLoading(true); setSlot(null);
    try {
      const sids = (appt.service_ids || []).join(",");
      const { data } = await api.get(`/portal/availability?date=${date}&service_ids=${sids}&operator_id=${appt.operator_id || ""}&exclude_appt_id=${appt.id}`);
      setAvail(data);
    } catch (e) { toast.error("Errore nel caricamento della disponibilità"); }
    finally { setLoading(false); }
  }, [appt, date]);

  useEffect(() => {
    if (appt) { setDate(dstr(new Date(Date.now() + 86400000))); setSlot(null); setAvail(null); }
  }, [appt]);

  useEffect(() => { if (appt) loadAvail(); }, [appt, loadAvail]);

  const confirm = async () => {
    if (!slot) return;
    setSaving(true);
    try {
      await api.put(`/portal/appointments/${appt.id}`, { action: "modify", start: slot.start });
      toast.success("Appuntamento riprogrammato");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossibile riprogrammare"); }
    finally { setSaving(false); }
  };

  const next14 = Array.from({ length: 14 }, (_, i) => new Date(Date.now() + (i + 1) * 86400000));

  return (
    <Dialog open={!!appt} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Riprogramma appuntamento</DialogTitle>
          <DialogDescription>
            {appt && (<>{(appt.service_names || []).join(", ")} · con {appt.operator_name || "il tuo operatore"}. Puoi spostare fino a {RESCHEDULE_MIN_HOURS} ore prima.</>)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Scegli il giorno</div>
            <div className="flex gap-2 overflow-x-auto hi-scroll pb-1">
              {next14.map((d) => { const k = dstr(d); return (
                <button key={k} onClick={() => setDate(k)} data-testid={`reschedule-day-${k}`}
                  className={`shrink-0 rounded-xl border px-3 py-2 text-center transition-colors ${date === k ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold-soft))]" : "border-border hover:bg-accent"}`}>
                  <div className="text-[10px] uppercase">{d.toLocaleDateString("it-IT", { weekday: "short" })}</div>
                  <div className="font-semibold">{d.getDate()}</div>
                  <div className="text-[10px] text-muted-foreground">{d.toLocaleDateString("it-IT", { month: "short" })}</div>
                </button>); })}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Orari disponibili</div>
            {loading ? (
              <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--hi-gold))]" /></div>
            ) : ((avail?.slots || []).length === 0 ? (
              <Card className="p-4 text-center text-sm text-muted-foreground" data-testid="reschedule-no-slots">
                {avail?.reason_text || "Nessuno slot disponibile in questa data."}
              </Card>
            ) : (
              <div className="grid grid-cols-4 gap-2 max-h-52 overflow-y-auto hi-scroll">
                {avail.slots.map((s) => (
                  <button key={s.time} onClick={() => setSlot(s)} data-testid={`reschedule-slot-${s.time}`}
                    className={`rounded-lg border py-2 text-sm font-medium transition-colors ${slot?.time === s.time ? "border-[hsl(var(--hi-gold))] bg-[hsl(var(--hi-gold-soft))]" : "border-border hover:bg-accent"}`}>
                    {s.time}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {slot && (
            <Card className="p-3 flex items-center gap-2 border-[hsl(var(--hi-gold)/0.4)]" data-testid="reschedule-summary">
              <Clock className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
              <span className="text-sm">Nuovo orario: <span className="font-semibold">{new Date(slot.start).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })} · {slot.time}</span></span>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="reschedule-cancel">Annulla</Button>
          <Button onClick={confirm} disabled={!slot || saving} data-testid="reschedule-confirm">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}Conferma spostamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
